import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService.js';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

function transformOrganisationTreeData(items) {
	if (!Array.isArray(items) || items.length === 0) return [];

	const transformed = items.map((item) => ({
		id: item.id,
		label: item.label,
		title: item.label,
		reference: item.reference,
		parentId: item.parentId,
		depth: item.level,
		typeId: item.typeId,
		typeLabel: item.typeLabel,
		children: [],
	}));

	const itemMap = new Map();
	transformed.forEach((item) => itemMap.set(item.id, item));

	const roots = [];
	transformed.forEach((item) => {
		if (item.parentId === null || item.parentId === 0 || !itemMap.has(item.parentId)) {
			roots.push(item);
		} else {
			const parent = itemMap.get(item.parentId);
			if (parent) parent.children.push(item);
		}
	});

	return roots;
}

const organisationCache = new CacheManager('VueCache', 'cache', {
	maxAge: 7 * 24 * 60 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'Organisation',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.organisations.`,
});

export async function getOrganisations() {
	const cacheKey = 'organisations';

	const memCached = organisationCache.getFromMemory(cacheKey);
	if (memCached) {
		return memCached;
	}

	try {
		const legacyData = await iframeDataBridge.getStorageAsync('ORGANISATIONS');
		if (legacyData) {
			log('info', 'Organisation', '📦 Seeding Vue cache from legacy storage (ORGANISATIONS)');
			await organisationCache.put(cacheKey, legacyData);
			organisationCache.setInMemory(cacheKey, legacyData);
			return legacyData;
		}
	} catch (legacyError) {
		log('warn', 'Organisation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
	}

	try {
		const cachedOrganisations = await organisationCache.get(cacheKey);
		if (cachedOrganisations) {
			organisationCache.setInMemory(cacheKey, cachedOrganisations);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'Organisation', '📴 Offline: Returning cached organisations');
				return cachedOrganisations;
			}

			setTimeout(() => refreshOrganisationsInBackground(), 100);
			return cachedOrganisations;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Organisation', '📴 Offline: No cached organisations available');
			return [];
		}

		return await fetchOrganisationsFromAPI();
	} catch (error) {
		log('error', 'Organisation', `❌ Error getting organisations: ${error.message}`);

		try {
			const fallbackOrganisations = await organisationCache.get(cacheKey);
			if (fallbackOrganisations) {
				log('info', 'Organisation', '💾 Using fallback cached organisations');
				return fallbackOrganisations;
			}
		} catch (cacheError) {
			log('error', 'Organisation', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchOrganisationsFromAPI() {
	try {
		const apiUrl = getApiUrl('Core/Organisations');
		const response = await authenticatedFetch(
			apiUrl,
			{
				method: 'GET',
			},
			'tokenSales'
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const organisations = await response.json();
		const cacheKey = 'organisations';
		await organisationCache.put(cacheKey, organisations);
		organisationCache.setInMemory(cacheKey, organisations);

		log('success', 'Organisation', `✅ Fetched and cached ${organisations.length} organisations`);
		return organisations;
	} catch (error) {
		log('error', 'Organisation', `❌ Failed to fetch organisations from API: ${error.message}`);
		throw error;
	}
}

export async function getOrganisationMaxLevel() {
	const cacheKey = 'organisation-max-level';

	const memCached = organisationCache.getFromMemory(cacheKey);
	if (memCached) {
		return memCached;
	}

	try {
		const cachedOrganisationMaxLevel = await organisationCache.get(cacheKey);
		if (cachedOrganisationMaxLevel) {
			organisationCache.setInMemory(cacheKey, cachedOrganisationMaxLevel);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'Organisation', '📴 Offline: Returning cached organisation max level');
				return cachedOrganisationMaxLevel;
			}

			setTimeout(() => refreshOrganisationsInBackground(), 100);
			return cachedOrganisationMaxLevel;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Organisation', '📴 Offline: No cached organisation max level available');
			return null;
		}

		return await fetchOrganisationMaxLevelFromAPI();
	} catch (error) {
		log('error', 'Organisation', `❌ Error getting organisation max level: ${error.message}`);

		try {
			const fallbackOrganisationMaxLevel = await organisationCache.get(cacheKey);
			if (fallbackOrganisationMaxLevel) {
				log('info', 'Organisation', '💾 Using fallback cached organisation max level');
				return fallbackOrganisationMaxLevel;
			}
		} catch (cacheError) {
			log('error', 'Organisation', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return null;
	}
}

async function fetchOrganisationMaxLevelFromAPI() {
	try {
		const apiUrl = getApiUrl('Core/Organisations/MaxLevel');
		const response = await authenticatedFetch(
			apiUrl,
			{
				method: 'GET',
			},
			'tokenSales'
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const maxLevel = await response.json();
		const cacheKey = 'organisation-max-level';
		await organisationCache.put(cacheKey, maxLevel);
		organisationCache.setInMemory(cacheKey, maxLevel);
		log('success', 'Organisation', `✅ Fetched and cached organisation max level: ${maxLevel}`);
		return maxLevel;
	} catch (error) {
		log('error', 'Organisation', `❌ Failed to fetch organisation max level from API: ${error.message}`);
		throw error;
	}
}

async function refreshOrganisationsInBackground() {
	try {
		await fetchOrganisationsFromAPI();
		log('success', 'Organisation', '🔄 Background refresh of organisations completed');
	} catch (error) {
		log('warn', 'Organisation', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshOrganisations() {
	const cacheKey = 'organisations';

	organisationCache.deleteFromMemory(cacheKey);
	await organisationCache.delete(cacheKey);
}

function filterOrganisationSubtree(flat, rootId) {
	const result = [];
	const queue = [rootId];

	while (queue.length > 0) {
		const currentId = queue.shift();
		const org = flat.find((o) => o.id === currentId);
		if (!org) continue;
		result.push(org);
		flat.forEach((o) => {
			if (o.parentId === currentId) queue.push(o.id);
		});
	}

	return result;
}

export async function getOrganisationsTree() {
	const flat = await getOrganisations();
	const userOrgId = iframeDataBridge.getCurrentUser()?.organisationId;

	if (userOrgId && flat.some((o) => o.id === userOrgId)) {
		return transformOrganisationTreeData(filterOrganisationSubtree(flat, userOrgId));
	}

	return transformOrganisationTreeData(flat);
}

export async function clearOrganisationCache() {
	await organisationCache.clearAll();
}

export async function getOrganisationCacheStats() {
	return await organisationCache.getStats();
}

export async function cleanOldOrganisationCache(maxAgeInDays = 7) {
	await organisationCache.cleanOld(maxAgeInDays);
}

cleanOldOrganisationCache();
